import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  loadRawData,
  loadPivotReport,
  clearReportData,
} from "../../../../actions";
import { exportPivotXlsx, toModel, transpose } from "./PivotTable/pivotModel";
import { ALL_WEEKS, weeklyReport, weeksOf } from "./PivotTable/weeklyModel";
import { speciesByWeek } from "./PivotTable/speciesByWeekModel";
import { useWebServer } from "../../../../providers";
import { ianaTimezone } from "../../../../utils/dataDownload";
import { rawDataColumns } from "../../../../utils/headers";
import { useAllReportFields } from "./Filters/useReportFields";
import { Redirect, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { format } from "date-fns";
import ROUTES from "../../../AppRouter/routes";
import { SITE_DIMENSIONS, definitionForLegacyButton, pivotFor, reportDefinition } from "./definitions";
import { decodeFilters, isPopout, reportPath } from "./reportUrl";
import {
  DEFAULT_FILTERS,
  activeFilterCount,
  describeDates,
  normalizeFilters,
  toRequestFilter,
} from "./Filters/filterState";
import { downloadRawData } from "../../../../utils/download";
import { downloadXlsx } from "../../../../utils/spreadsheet";
import {
  downloadChartPng,
} from "../../../../utils/chartExport";
import { displayErrorNotification } from "../../../../actions/notifications";
import { Reports } from "./Reports";
import { downloadTemplate, previewTemplate } from "./Templates/templateApi";
import { dictionaryRows } from "./Dictionary/DataDictionary";
import { useSpecimenBrowser } from "./Specimens/useSpecimenBrowser";
import { cellClauses, cellTitle, withClauses } from "./Specimens/cellCriteria";
import { ContributedEditor } from "./Specimens/ContributedEditor";
import { saveContributed } from "./Specimens/specimenApi";
import { displaySuccessNotification } from "../../../../actions/notifications";

import {
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid";

// useGridApiRef() hands back a ref object that only fills in once a DataGrid
// mounts against it, so `apiRef` alone is always truthy - chart reports render
// no grid at all and leave `current` null, which the selector dereferences.
const getFilteredRows = (apiRef) => {
  if (!apiRef?.current) {
    return null;
  }
  const filteredRowsIds = gridFilteredSortedRowIdsSelector(apiRef);
  return filteredRowsIds.map((id) => apiRef.current.getRow(id));
};

const EMPTY_REPORT = [];

// The first calendar day the report's dates ask for, or null for an open
// start (see weeklyModel: the week before it is not loaded).
const reportRangeStart = (filters) => filters?.dates?.from ?? null;

const generatedLabel = (iso) => (iso ? `Generated ${format(new Date(iso), "MMM d, yyyy, h:mm a")}` : null);

const Index = ({
  timeZone,
  loadRawData,
  loadPivotReport,
  clearReportData,
  data: reportData,
  loading,
  generatedAt,
  displayErrorNotification,
  displaySuccessNotification,
}) => {

  const {
    sendOrganizationRequest,
    downloadOrganizationDataWithRawData,
  } = useWebServer();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch(ROUTES.report);
  const definition = reportDefinition(match?.params?.reportType);
  const slug = definition?.slug;
  // the report last run, which is shown only while it is the one open
  const [ranSlug, setRanSlug] = useState(null);
  const showReport = Boolean(slug) && ranSlug === slug;
  const popout = isPopout(location.search);
  // The filters are kept in the address (?f=), so a reload, a copied link or
  // a pop-out shows the same report. `draft` is what the panel is editing;
  // `generatedWith` what the shown report was made with.
  const linked = useRef(new URLSearchParams(location.search).get("f"));
  const [draft, setDraft] = useState(() => normalizeFilters(decodeFilters(linked.current)));
  const [generatedWith, setGeneratedWith] = useState(null);
  // A pop-out, or a link that carries filters, opens on the report with the
  // panel folded away; a fresh report opens with the panel showing.
  const [filtersOpen, setFiltersOpen] = useState(!popout && !linked.current);
  // a pivot report shown with its rows and columns swapped
  const [transposed, setTransposed] = useState(false);
  // the weekly report's week, or ALL_WEEKS; null until the data is in
  const [week, setWeek] = useState(null);
  // a time chart's grain and drawing
  const [grain, setGrain] = useState("month");
  const [chartMode, setChartMode] = useState("line");
  // the series a chart's legend has hidden
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());
  const toggleSeries = (key) =>
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // a template's first rows, as its file will have them
  const [templatePreview, setTemplatePreview] = useState({ data: null, loading: false });
  const [exporting, setExporting] = useState(false);

  // what a report's collection sites are grouped by: site, county or group
  const [siteDim, setSiteDim] = useState("collectionSite");
  // what is asked of the pivot: the definition's dimensions, with the time
  // axis at the chosen grain and the sites grouped as chosen
  const pivotRequest = useMemo(() => pivotFor(definition, { grain, siteDim }), [definition, grain, siteDim]);
  // the field registry: raw data's columns, the dictionary, "how this is
  // calculated"
  const fieldRegistry = useAllReportFields();
  const reportFields = fieldRegistry.fields;
  const rawColumns = useMemo(() => rawDataColumns(reportFields), [reportFields]);
  // which raw data columns show: the registry's defaults once it is in, then
  // whatever the user chooses
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  useEffect(() => {
    if (!rawColumns.length) return;
    setColumnVisibilityModel((current) =>
      Object.keys(current).length ? current : Object.fromEntries(rawColumns.map((c) => [c.field, !c.defaultHide]))
    );
  }, [rawColumns]);

  const dataGridRef = useGridApiRef();
  const specimenBrowser = useSpecimenBrowser(ianaTimezone(timeZone));

  useEffect(() => {
    return () => {
      clearReportData();
    };
    // eslint-disable-next-line
  }, []);

  // the rows as the server names them: regions by display name
  const data = reportData;
  // a pivot report's counts as returned, and as its table shows them
  const pivotData = useMemo(
    () => (showReport && definition.pivot && reportData && !Array.isArray(reportData) ? toModel(reportData) : null),
    [showReport, definition, reportData]
  );
  const weeks = useMemo(
    () => (definition?.view === "weekly" && pivotData ? weeksOf(pivotData) : []),
    [definition, pivotData]
  );
  // a fresh weekly report opens on its latest week; one run again (after
  // saving a contributed ID, say) stays on the week the user chose
  useEffect(() => {
    if (!weeks.length) return;
    setWeek((current) => (current === ALL_WEEKS || weeks.includes(current) ? current : weeks[weeks.length - 1]));
  }, [weeks]);
  const pivotModel = useMemo(() => {
    if (!pivotData) return null;
    let model = pivotData;
    if (definition.view === "weekly") {
      model = weeklyReport(model, {
        week: week ?? ALL_WEEKS,
        rangeStart: reportRangeStart(generatedWith),
      });
    }
    if (transposed) model = transpose(model);
    if (definition.view === "speciesByWeek") model = speciesByWeek(model);
    return model;
  }, [definition, pivotData, transposed, week, generatedWith]);

  // there is something to download once the shown report has rows
  const readyDownload = definition?.template
    ? !templatePreview.loading && templatePreview.data?.total > 0 && !exporting
    : !loading && (definition?.pivot ? pivotModel?.rows.length > 0 : data.length > 0 && rawColumns.length > 0);

  // a template's file, built on the server for the filters it was previewed with
  const downloadTemplateFile = async () => {
    setExporting(true);
    try {
      await downloadTemplate(
        sendOrganizationRequest,
        definition.template,
        toRequestFilter(generatedWith ?? draft, ianaTimezone(timeZone)),
        "xlsx"
      );
    } catch (err) {
      displayErrorNotification(err?.response?.data?.error || "The file could not be made.");
    } finally {
      setExporting(false);
    }
  };

  const previewRequest = useRef(0);

  // Run the report with `filters`: a template previews its file, raw data
  // loads its rows, everything else is a pivot. The dictionary has nothing
  // to run.
  const runReport = (filters = draft, request = pivotRequest) => {
    if (definition.kind === "dictionary") return;
    setRanSlug(definition.slug);
    setGeneratedWith(filters);
    const body = toRequestFilter(filters, ianaTimezone(timeZone));
    if (definition?.template) {
      const preview = ++previewRequest.current;
      setTemplatePreview({ data: null, loading: true });
      previewTemplate(sendOrganizationRequest, definition.template, body)
        .then((data) => {
          if (preview === previewRequest.current) setTemplatePreview({ data, loading: false, at: new Date().toISOString() });
        })
        .catch((err) => {
          if (preview !== previewRequest.current) return;
          setTemplatePreview({ data: null, loading: false });
          displayErrorNotification(err?.response?.data?.error || "The template could not be previewed.");
        });
      return;
    }
    if (request) {
      loadPivotReport(sendOrganizationRequest, body, request);
    } else {
      loadRawData(downloadOrganizationDataWithRawData, body);
    }
  };

  // Arriving at a report, by a link, the report menu or Back and Forward,
  // shows nothing of the one before. Filters in the address are what it was
  // run with: run it again with them. Without, the panel keeps what it had.
  useEffect(() => {
    if (!slug) return;
    clearReportData();
    previewRequest.current += 1;
    setTemplatePreview({ data: null, loading: false });
    setRanSlug(null);
    setTransposed(false);
    setHiddenSeries(new Set());
    setGeneratedWith(null);
    const linkedFilters = new URLSearchParams(location.search).get("f");
    if (linkedFilters) {
      const filters = normalizeFilters(decodeFilters(linkedFilters));
      setDraft(filters);
      runReport(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const isChart = definition?.kind === "chart";

  // The drawn chart hands its download spec here (see useChartExport).
  const chartExport = useRef(null);
  const registerChartExport = useCallback((getSpec) => {
    chartExport.current = getSpec;
  }, []);

  const handleDownloadChartImage = async () => {
    const spec = chartExport.current?.();
    if (!spec) return;
    try {
      await downloadChartPng({
        container: document.getElementById("chart-container"),
        title: definition.title,
        subtitle: describeDates(generatedWith ?? draft),
        legend: spec.legend,
      });
    } catch (err) {
      displayErrorNotification(err?.message || "The chart could not be downloaded");
    }
  };

  const handleDownloadChartData = () => {
    const spec = chartExport.current?.();
    if (spec?.dataRows?.length) {
      downloadXlsx({ title: `${definition.title} chart data`, rows: spec.dataRows });
    }
  };

  // Generate (or Preview): with no filters set, ask first - the report would
  // cover every specimen the organization has imaged
  const [confirmUnfiltered, setConfirmUnfiltered] = useState(false);
  const generate = () => {
    setConfirmUnfiltered(false);
    // the address says what was run
    history.replace(reportPath(definition.slug, { popout, filters: draft }));
    runReport(draft);
  };
  const handleGenerateReport = () => {
    if (activeFilterCount(draft) === 0) setConfirmUnfiltered(true);
    else generate();
  };

  // a new grain is a new question for the pivot: ask it again, with the
  // filters the shown chart was made with
  const changeGrain = (next) => {
    setGrain(next);
    if (showReport && generatedWith) {
      runReport(generatedWith, pivotFor(definition, { grain: next, siteDim }));
    }
  };

  // likewise a new way of grouping the sites
  const changeSiteDim = (next) => {
    setSiteDim(next);
    if (showReport && generatedWith) {
      runReport(generatedWith, pivotFor(definition, { grain, siteDim: next }));
    }
  };
  const siteDimLabel = SITE_DIMENSIONS.find((d) => d.value === siteDim)?.label ?? "Collection site";

  // a report that had run carries on as the new one, with the same filters
  const handleSwitchReport = (next) => {
    if (next === slug) return;
    const filters = reportDefinition(next)?.kind === "dictionary" ? null : generatedWith;
    history.push(reportPath(next, { popout, filters }));
  };

  const handlePopout = () => {
    window.open(
      reportPath(definition.slug, { popout: true, filters: generatedWith ?? draft }),
      "_blank",
      "noopener"
    );
  };

  const handleDownloadClicked = () => {
    if (isChart) {
      handleDownloadChartImage();
      return;
    }

    if (pivotModel) {
      // a table's file takes the plain title; its chart twin's data adds
      // "chart data"
      exportPivotXlsx(
        pivotModel,
        definition.view === "weekly" && week && week !== ALL_WEEKS
          ? `${definition.title} week of ${week}`
          : definition.title
      );
      return;
    }

    // what the grid shows: its visible columns in order, its rows as sorted
    const columns = dataGridRef?.current
      ? gridVisibleColumnDefinitionsSelector(dataGridRef)
      : rawColumns.filter((c) => columnVisibilityModel[c.field] !== false);
    downloadRawData(getFilteredRows(dataGridRef) ?? data, columns, definition.title);
  };

  // A count opens the specimens behind it: the report's own filters with the
  // cell's row and column values ANDed in. Counts no condition can express
  // (weeks of the year) stay plain numbers.
  const openCell = (cell) => {
    if (!generatedWith || !pivotModel) return;
    const clauses = cellClauses(cell, pivotModel.rowDims, pivotModel.colDims);
    if (!clauses) return;
    specimenBrowser.open({
      title: cellTitle(cell),
      filter: withClauses(toRequestFilter(generatedWith, ianaTimezone(timeZone)), clauses),
    });
  };
  // a raw data row opens its specimen, stepping through the grid's rows as
  // shown (filtered and sorted)
  const openRow = (row) => {
    const rows = getFilteredRows(dataGridRef) ?? data;
    const keys = rows.map((r) => ({ trayId: r.tray, specimenId: r.specimen }));
    const index = rows.findIndex((r) => r.tray === row.tray && r.specimen === row.specimen);
    specimenBrowser.open({ title: definition.title, keys, index: Math.max(0, index) });
  };

  // Save a contributed ID from the panel: show the specimen as it now
  // reports, rerun the report so its counts follow, and with `next` go on.
  const saveFromPanel = async (patch, { next }) => {
    const shown = specimenBrowser.specimen;
    const key = { trayId: shown.tray.id, specimenId: shown.specimen.id };
    const updated = await saveContributed(sendOrganizationRequest, key.trayId, key.specimenId, patch);
    specimenBrowser.setSpecimen({ ...updated, images: shown.images });
    displaySuccessNotification(`Saved: ${updated.specimen.final.species}`);
    if (generatedWith) runReport(generatedWith);
    if (next) await specimenBrowser.advanceFrom(key);
  };
  const renderEditor = (data) => (
    <ContributedEditor
      data={data}
      onSave={saveFromPanel}
      canAdvance={Boolean(specimenBrowser.source?.filter) || specimenBrowser.hasNext}
    />
  );

  // /reports?button=Raw-Data, the old address: send it to /reports/raw-data
  if (!definition) {
    const legacy = definitionForLegacyButton(
      new URLSearchParams(location.search).get("button")
    );
    return <Redirect to={legacy ? reportPath(legacy.slug) : ROUTES.dataTab} />;
  }

  const subtitle =
    definition.kind === "dictionary"
      ? "What each field in the reports and the raw data download means, and where it comes from"
      : [describeDates(generatedWith ?? draft), generatedLabel(definition.template ? templatePreview.at : generatedAt)]
          .filter(Boolean)
          .join(" · ");

  return (
    <Reports
      definition={definition}
      subtitle={subtitle}
      popout={popout}
      filtersOpen={filtersOpen}
      setFiltersOpen={setFiltersOpen}
      filters={draft}
      onFiltersChange={setDraft}
      activeFilterCount={activeFilterCount(draft)}
      onResetFilters={() => setDraft({ ...DEFAULT_FILTERS })}
      onBack={() => history.push(ROUTES.dataTab)}
      onSwitchReport={handleSwitchReport}
      onPopout={handlePopout}
      timeZone={timeZone}
      loading={loading}
      handleGenerateReport={handleGenerateReport}
      unfilteredWarning={{
        open: confirmUnfiltered,
        onRunAnyway: generate,
        onCancel: () => setConfirmUnfiltered(false),
      }}
      isChart={isChart}
      pivotModel={pivotModel}
      weekPicker={
        definition.view === "weekly" && weeks.length
          ? { weeks, week: week ?? ALL_WEEKS, onChange: setWeek, weekStart: reportData?.meta?.weekStart }
          : null
      }
      transposed={transposed}
      onToggleTranspose={
        definition.pivot && definition.kind !== "chart" ? () => setTransposed(!transposed) : null
      }
      chartView={
        definition.views
          ? {
              options: [`By ${siteDimLabel.toLowerCase()}`, definition.views[1]],
              value: transposed ? 1 : 0,
              onChange: (i) => setTransposed(i === 1),
            }
          : null
      }
      siteGrouping={
        definition.siteAxis ? { options: SITE_DIMENSIONS, value: siteDim, onChange: changeSiteDim } : null
      }
      // a site in two groups counts in both; say which sites did
      overlapNote={
        siteDim === "siteGroup" && reportData?.meta?.sitesInSeveralGroups?.length
          ? reportData.meta.sitesInSeveralGroups
          : null
      }
      timeChart={
        definition.grains
          ? {
              grains: definition.grains,
              grain,
              onGrain: changeGrain,
              mode: chartMode,
              onMode: setChartMode,
              hidden: hiddenSeries,
              onToggle: toggleSeries,
            }
          : null
      }
      // a table's counts open the specimens behind them
      onCellClick={definition.pivot && definition.kind !== "chart" ? openCell : null}
      onRowClick={definition.pivot ? null : openRow}
      specimens={{ browser: specimenBrowser, renderEditor }}
      readyDownload={readyDownload}
      onOpenDictionary={() => history.push(reportPath("dictionary", { popout }))}
      onDownloadDictionary={() => downloadXlsx({ title: definition.title, rows: dictionaryRows(reportFields) })}
      template={
        definition.template
          ? { preview: templatePreview.data, loading: templatePreview.loading, onDownload: downloadTemplateFile, exporting }
          : null
      }
      handleDownloadClicked={handleDownloadClicked}
      handleDownloadChartData={handleDownloadChartData}
      registerChartExport={registerChartExport}
      rawData={data}
      onDownloadData={runReport}
      columnVisibilityModel={columnVisibilityModel}
      rawColumns={rawColumns}
      reportFields={reportFields}
      fieldsFailed={fieldRegistry.failed}
      onRetryFields={fieldRegistry.retry}
      setColumnVisibilityModel={setColumnVisibilityModel}
      showReport={showReport}
      dataGridRef={dataGridRef}
    />
  );
};

const mapStateToProps = (state) => ({
  timeZone: state.user.timeZone,
  data: state.report?.report?.data || EMPTY_REPORT,
  loading: state.report?.report?.loading || false,
  generatedAt: state.report?.report?.generatedAt ?? null,
});

export default connect(mapStateToProps, {
  loadRawData,
  loadPivotReport,
  clearReportData,
  displayErrorNotification,
  displaySuccessNotification,
})(Index);
